import React, { useEffect, useMemo } from 'react';
import { Accordion, Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    FiCalendar,
    FiCloudRain,
    FiClock,
    FiMapPin,
    FiPercent,
    FiZap,
} from 'react-icons/fi';
import {
    DATA_SOURCE_OPTIONS,
    TRIGGER_TYPE_OPTIONS,
    UNIT_OPTIONS,
    defaultSubCoverageFormValues,
    subCoverageCreateSchema,
} from './subCoverage.schema';

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
    value: String(hour),
    label: `${String(hour).padStart(2, '0')}:00`,
}));

const FieldError = ({ message }) =>
    message ? (
        <div className="scc-field-error" role="alert">
            <span aria-hidden="true">⚠</span>
            <span>{message}</span>
        </div>
    ) : null;

const RequiredMark = () => (
    <span className="scc-required" aria-hidden="true">
        *
    </span>
);

const SectionCard = ({ id, title, icon: Icon, children, description }) => (
    <section className="scc-section" aria-labelledby={id}>
        <div className="scc-section__header">
            {Icon ? (
                <span className="scc-section__icon" aria-hidden="true">
                    <Icon size={16} />
                </span>
            ) : null}
            <div>
                <h2 id={id} className="scc-section__title">
                    {title}
                </h2>
                {description ? <p className="scc-section__desc">{description}</p> : null}
            </div>
        </div>
        <div className="scc-section__body">{children}</div>
    </section>
);

const SubBlock = ({ title, children, className = '' }) => (
    <div className={`scc-subblock ${className}`.trim()}>
        {title ? <h3 className="scc-subblock__title">{title}</h3> : null}
        {children}
    </div>
);

const InputWithUnit = ({ unit, children }) => (
    <div className="scc-input-with-unit">
        {children}
        {unit ? <span className="scc-unit">{unit}</span> : null}
    </div>
);

const getPartnerLabel = (partner) =>
    partner?.company_name ||
    `${partner?.partner_first_name || partner?.first_name || ''} ${partner?.partner_last_name || partner?.last_name || ''}`.trim() ||
    partner?.email ||
    '—';

const getPartnerId = (partner) =>
    partner?.user_uuid || partner?.partner_id || partner?.id;

const getReferenceLabel = (ref) => {
    if (!ref) return '—';
    const name = ref.name || ref.reference_name || `Référence ${ref.reference_id || ref.id}`;
    const code = ref.reference_code ? ` (${ref.reference_code})` : '';
    const category = ref.category ? ` — ${ref.category}` : '';
    return `${name}${code}${category}`;
};

/**
 * Formulaire de création — structure UX métier (5 sections).
 */
const SubCoverageCreateForm = ({
    partners,
    references,
    loadingPartners,
    loadingReferences,
    selectedPartnerId,
    selectedReferenceId,
    onPartnerChange,
    onReferenceChange,
    contextLocked = false,
    submitting,
    submitError,
    successMessage,
    apiFieldErrors = {},
    onSubmit,
    onCancel,
}) => {
    const {
        register,
        handleSubmit,
        setValue,
        setError,
        clearErrors,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(subCoverageCreateSchema),
        defaultValues: {
            ...defaultSubCoverageFormValues,
            partner_id: selectedPartnerId || '',
            reference_id: selectedReferenceId || '',
        },
        mode: 'onBlur',
    });

    const unit = watch('unit');
    const unitLabel =
        UNIT_OPTIONS.find((opt) => opt.value === String(unit || '').toLowerCase())?.label ||
        unit ||
        'mm';

    const selectedPartner = useMemo(
        () => partners.find((p) => String(getPartnerId(p)) === String(selectedPartnerId)),
        [partners, selectedPartnerId]
    );
    const selectedReference = useMemo(
        () =>
            references.find(
                (ref) => String(ref.reference_id || ref.id) === String(selectedReferenceId)
            ),
        [references, selectedReferenceId]
    );

    useEffect(() => {
        setValue('partner_id', selectedPartnerId || '', { shouldValidate: false });
    }, [selectedPartnerId, setValue]);

    useEffect(() => {
        setValue('reference_id', selectedReferenceId || '', { shouldValidate: false });
    }, [selectedReferenceId, setValue]);

    useEffect(() => {
        Object.entries(apiFieldErrors || {}).forEach(([field, message]) => {
            setError(field, { type: 'server', message });
        });
    }, [apiFieldErrors, setError]);

    const handlePartnerSelect = (event) => {
        const value = event.target.value;
        onPartnerChange(value);
        setValue('partner_id', value, { shouldValidate: true });
        setValue('reference_id', '', { shouldValidate: false });
        clearErrors(['partner_id', 'reference_id']);
    };

    const handleReferenceSelect = (event) => {
        const value = event.target.value;
        onReferenceChange(value);
        setValue('reference_id', value, { shouldValidate: true });
        clearErrors('reference_id');
    };

    return (
        <Form
            className="scc-form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            aria-busy={submitting}
        >
            {successMessage ? (
                <Alert variant="success" role="status" className="scc-alert">
                    {successMessage}
                </Alert>
            ) : null}

            {submitError ? (
                <Alert variant="danger" role="alert" className="scc-alert">
                    {submitError}
                </Alert>
            ) : null}

            {/* 1. Référence et période */}
            <SectionCard
                id="scc-reference-period"
                title="Référence et période"
                icon={FiCalendar}
                description="Définissez le contexte et la période de couverture de la sous-couverture."
            >
                <SubBlock title="Référence">
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Group controlId="partner_id">
                                <Form.Label>
                                    Partenaire <RequiredMark />
                                </Form.Label>
                                {contextLocked ? (
                                    <div className="scc-readonly" aria-live="polite">
                                        {getPartnerLabel(selectedPartner)}
                                    </div>
                                ) : (
                                    <Form.Select
                                        value={selectedPartnerId || ''}
                                        onChange={handlePartnerSelect}
                                        disabled={submitting || loadingPartners}
                                        isInvalid={!!errors.partner_id}
                                        aria-required="true"
                                        aria-invalid={!!errors.partner_id}
                                    >
                                        <option value="">Sélectionner un partenaire…</option>
                                        {partners.map((partner) => {
                                            const id = getPartnerId(partner);
                                            return (
                                                <option key={id} value={id}>
                                                    {getPartnerLabel(partner)}
                                                </option>
                                            );
                                        })}
                                    </Form.Select>
                                )}
                                <input type="hidden" {...register('partner_id')} />
                                <FieldError message={errors.partner_id?.message} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="reference_id">
                                <Form.Label>
                                    Référence <RequiredMark />
                                </Form.Label>
                                {contextLocked ? (
                                    <div className="scc-readonly" aria-live="polite">
                                        {getReferenceLabel(selectedReference)}
                                    </div>
                                ) : loadingReferences ? (
                                    <div className="scc-inline-loading" aria-live="polite">
                                        <Spinner size="sm" animation="border" />
                                        <span>Chargement des références…</span>
                                    </div>
                                ) : (
                                    <Form.Select
                                        value={selectedReferenceId || ''}
                                        onChange={handleReferenceSelect}
                                        disabled={
                                            submitting ||
                                            !selectedPartnerId ||
                                            references.length === 0
                                        }
                                        isInvalid={!!errors.reference_id}
                                        aria-required="true"
                                        aria-invalid={!!errors.reference_id}
                                    >
                                        <option value="">
                                            {!selectedPartnerId
                                                ? 'Sélectionnez d’abord un partenaire…'
                                                : references.length === 0
                                                  ? 'Aucune référence disponible'
                                                  : 'Sélectionner une référence…'}
                                        </option>
                                        {references.map((ref) => {
                                            const refId = ref.reference_id || ref.id;
                                            return (
                                                <option key={refId} value={refId}>
                                                    {getReferenceLabel(ref)}
                                                </option>
                                            );
                                        })}
                                    </Form.Select>
                                )}
                                <input type="hidden" {...register('reference_id')} />
                                <FieldError message={errors.reference_id?.message} />
                            </Form.Group>
                        </Col>
                    </Row>
                </SubBlock>

                <SubBlock title="Période de couverture">
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Group controlId="sub_coverage_start_date">
                                <Form.Label>
                                    Date de début <RequiredMark />
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    {...register('sub_coverage_start_date')}
                                    disabled={submitting}
                                    isInvalid={!!errors.sub_coverage_start_date}
                                    aria-required="true"
                                />
                                <FieldError message={errors.sub_coverage_start_date?.message} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="sub_coverage_end_date">
                                <Form.Label>
                                    Date de fin <RequiredMark />
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    {...register('sub_coverage_end_date')}
                                    disabled={submitting}
                                    isInvalid={!!errors.sub_coverage_end_date}
                                    aria-required="true"
                                />
                                <FieldError message={errors.sub_coverage_end_date?.message} />
                            </Form.Group>
                        </Col>
                    </Row>
                </SubBlock>
            </SectionCard>

            {/* 2. Déclenchement météo */}
            <SectionCard
                id="scc-weather-trigger"
                title="Déclenchement météo"
                icon={FiZap}
                description="Définissez le type de déclenchement et les conditions météorologiques."
            >
                <Row className="g-3">
                    <Col md={4}>
                        <Form.Group controlId="trigger_type">
                            <Form.Label>
                                Type de déclenchement <RequiredMark />
                            </Form.Label>
                            <Form.Select
                                {...register('trigger_type')}
                                disabled={submitting}
                                isInvalid={!!errors.trigger_type}
                                aria-required="true"
                            >
                                <option value="">Sélectionner…</option>
                                {TRIGGER_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <FieldError message={errors.trigger_type?.message} />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group controlId="data_source">
                            <Form.Label>
                                Source de données <RequiredMark />
                            </Form.Label>
                            <Form.Select
                                {...register('data_source')}
                                disabled={submitting}
                                isInvalid={!!errors.data_source}
                                aria-required="true"
                            >
                                <option value="">Sélectionner…</option>
                                {DATA_SOURCE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <FieldError message={errors.data_source?.message} />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group controlId="unit">
                            <Form.Label>
                                Unité <RequiredMark />
                            </Form.Label>
                            <Form.Select
                                {...register('unit')}
                                disabled={submitting}
                                isInvalid={!!errors.unit}
                                aria-required="true"
                            >
                                <option value="">Sélectionner…</option>
                                {UNIT_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <FieldError message={errors.unit?.message} />
                        </Form.Group>
                    </Col>
                </Row>

                <SubBlock title="Conditions de déclenchement" className="mt-3">
                    <div className="scc-weather-card">
                        <div className="scc-weather-card__header">
                            <span className="scc-weather-card__icon" aria-hidden="true">
                                <FiCloudRain size={16} />
                            </span>
                            <h3 className="scc-weather-card__title">Précipitations</h3>
                        </div>
                        <div className="scc-weather-card__grid">
                            <Form.Group controlId="trigger_low">
                                <Form.Label>Seuil de déclenchement</Form.Label>
                                <InputWithUnit unit={unitLabel || 'mm/h'}>
                                    <Form.Control
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="1,6"
                                        {...register('trigger_low')}
                                        disabled={submitting}
                                        isInvalid={!!errors.trigger_low}
                                    />
                                </InputWithUnit>
                                <FieldError message={errors.trigger_low?.message} />
                            </Form.Group>
                            <Form.Group controlId="prcp_trigger_number_hours">
                                <Form.Label>Nombre d’heures de déclenchement</Form.Label>
                                <InputWithUnit unit="heures">
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder="3"
                                        {...register('prcp_trigger_number_hours')}
                                        disabled={submitting}
                                        isInvalid={!!errors.prcp_trigger_number_hours}
                                    />
                                </InputWithUnit>
                                <FieldError message={errors.prcp_trigger_number_hours?.message} />
                            </Form.Group>
                        </div>
                    </div>
                </SubBlock>
            </SectionCard>

            {/* 3. Paramètres temporels */}
            <SectionCard
                id="scc-timing"
                title="Paramètres temporels"
                icon={FiClock}
                description="Planifiez le déclenchement et l’observation."
            >
                <Row className="g-3">
                    <Col md={3} sm={6}>
                        <Form.Group controlId="timing_hour">
                            <Form.Label>Heure de déclenchement</Form.Label>
                            <Form.Select
                                {...register('timing_hour')}
                                disabled={submitting}
                                isInvalid={!!errors.timing_hour}
                            >
                                <option value="">—</option>
                                {HOUR_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <FieldError message={errors.timing_hour?.message} />
                        </Form.Group>
                    </Col>
                    <Col md={3} sm={6}>
                        <Form.Group controlId="timing_day">
                            <Form.Label>
                                Jour de déclenchement <RequiredMark />
                            </Form.Label>
                            <Form.Control
                                type="number"
                                placeholder="0"
                                {...register('timing_day')}
                                disabled={submitting}
                                isInvalid={!!errors.timing_day}
                                aria-required="true"
                            />
                            <FieldError message={errors.timing_day?.message} />
                        </Form.Group>
                    </Col>
                    <Col md={3} sm={6}>
                        <Form.Group controlId="observation_timing_hour">
                            <Form.Label>
                                Heure d’observation <RequiredMark />
                            </Form.Label>
                            <Form.Select
                                {...register('observation_timing_hour')}
                                disabled={submitting}
                                isInvalid={!!errors.observation_timing_hour}
                                aria-required="true"
                            >
                                <option value="">Sélectionner…</option>
                                {HOUR_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <FieldError message={errors.observation_timing_hour?.message} />
                        </Form.Group>
                    </Col>
                    <Col md={3} sm={6}>
                        <Form.Group controlId="observation_timing_day">
                            <Form.Label>
                                Jour d’observation <RequiredMark />
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="+1"
                                {...register('observation_timing_day')}
                                disabled={submitting}
                                isInvalid={!!errors.observation_timing_day}
                                aria-required="true"
                                aria-describedby="observation_timing_day_help"
                            />
                            <Form.Text id="observation_timing_day_help" className="scc-help">
                                Indiquez 0 pour le même jour, +1 pour le lendemain, etc.
                            </Form.Text>
                            <FieldError message={errors.observation_timing_day?.message} />
                        </Form.Group>
                    </Col>
                </Row>
            </SectionCard>

            {/* 4. Indemnisation */}
            <SectionCard
                id="scc-indemnisation"
                title="Paramètres d’indemnisation"
                icon={FiPercent}
                description="Configurez les taux appliqués en cas de maintien ou d’annulation."
            >
                <Row className="g-3">
                    <Col md={4}>
                        <Form.Group controlId="siv_ratio_maintained">
                            <Form.Label>
                                Taux SIV maintenu <RequiredMark />
                            </Form.Label>
                            <InputWithUnit unit="%">
                                <Form.Control
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="50,00"
                                    {...register('siv_ratio_maintained')}
                                    disabled={submitting}
                                    isInvalid={!!errors.siv_ratio_maintained}
                                    aria-required="true"
                                    aria-describedby="siv_maintained_help"
                                />
                            </InputWithUnit>
                            <Form.Text id="siv_maintained_help" className="scc-help">
                                Valeur comprise entre 0 et 100 %.
                            </Form.Text>
                            <FieldError message={errors.siv_ratio_maintained?.message} />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group controlId="siv_ratio_cancelled">
                            <Form.Label>
                                Taux SIV annulé <RequiredMark />
                            </Form.Label>
                            <InputWithUnit unit="%">
                                <Form.Control
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="100,00"
                                    {...register('siv_ratio_cancelled')}
                                    disabled={submitting}
                                    isInvalid={!!errors.siv_ratio_cancelled}
                                    aria-required="true"
                                    aria-describedby="siv_cancelled_help"
                                />
                            </InputWithUnit>
                            <Form.Text id="siv_cancelled_help" className="scc-help">
                                Valeur comprise entre 0 et 100 %.
                            </Form.Text>
                            <FieldError message={errors.siv_ratio_cancelled?.message} />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group controlId="orakle_price_ratio_excl_tsca">
                            <Form.Label>
                                Ratio de prix hors TSCA <RequiredMark />
                            </Form.Label>
                            <Form.Control
                                type="text"
                                inputMode="decimal"
                                placeholder="5,73"
                                {...register('orakle_price_ratio_excl_tsca')}
                                disabled={submitting}
                                isInvalid={!!errors.orakle_price_ratio_excl_tsca}
                                aria-required="true"
                            />
                            <FieldError message={errors.orakle_price_ratio_excl_tsca?.message} />
                        </Form.Group>
                    </Col>
                </Row>
            </SectionCard>

            {/* 5. Localisation */}
            <SectionCard
                id="scc-location"
                title="Localisation"
                icon={FiMapPin}
                description="Indiquez où se déroule l’activité couverte."
            >
                <SubBlock title="Localisation de l’activité">
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Group controlId="activity_address">
                                <Form.Label>
                                    Adresse de l’activité <RequiredMark />
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="12 Rue de la Paix"
                                    {...register('activity_address')}
                                    disabled={submitting}
                                    isInvalid={!!errors.activity_address}
                                    aria-required="true"
                                />
                                <FieldError message={errors.activity_address?.message} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group controlId="city">
                                <Form.Label>
                                    Ville <RequiredMark />
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Paris"
                                    {...register('city')}
                                    disabled={submitting}
                                    isInvalid={!!errors.city}
                                    aria-required="true"
                                />
                                <FieldError message={errors.city?.message} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group controlId="country">
                                <Form.Label>
                                    Pays <RequiredMark />
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="France"
                                    {...register('country')}
                                    disabled={submitting}
                                    isInvalid={!!errors.country}
                                    aria-required="true"
                                />
                                <FieldError message={errors.country?.message} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group controlId="zip_code">
                                <Form.Label>Code postal</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="75008"
                                    {...register('zip_code')}
                                    disabled={submitting}
                                    isInvalid={!!errors.zip_code}
                                />
                                <FieldError message={errors.zip_code?.message} />
                            </Form.Group>
                        </Col>
                    </Row>
                </SubBlock>

                <SubBlock title="Coordonnées géographiques">
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Group controlId="geo_latitude">
                                <Form.Label>Latitude</Form.Label>
                                <Form.Control
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="48,8566"
                                    {...register('geo_latitude')}
                                    disabled={submitting}
                                    isInvalid={!!errors.geo_latitude}
                                />
                                <FieldError message={errors.geo_latitude?.message} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="geo_longitude">
                                <Form.Label>Longitude</Form.Label>
                                <Form.Control
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="2,3522"
                                    {...register('geo_longitude')}
                                    disabled={submitting}
                                    isInvalid={!!errors.geo_longitude}
                                />
                                <FieldError message={errors.geo_longitude?.message} />
                            </Form.Group>
                        </Col>
                    </Row>
                </SubBlock>
            </SectionCard>

            <Accordion className="scc-advanced" flush>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Paramètres avancés</Accordion.Header>
                    <Accordion.Body>
                        <p className="scc-advanced__hint">
                            Paramètres techniques rarement modifiés. Conservés pour compatibilité
                            complète avec le modèle métier.
                        </p>
                        <Row className="g-3">
                            <Col md={4}>
                                <Form.Group controlId="trigger_sup">
                                    <Form.Label>Seuil supérieur</Form.Label>
                                    <InputWithUnit unit={unitLabel}>
                                        <Form.Control
                                            type="text"
                                            inputMode="decimal"
                                            {...register('trigger_sup')}
                                            disabled={submitting}
                                            isInvalid={!!errors.trigger_sup}
                                        />
                                    </InputWithUnit>
                                    <FieldError message={errors.trigger_sup?.message} />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group controlId="prcp_trigger_hour_inf">
                                    <Form.Label>Heure de début</Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('prcp_trigger_hour_inf')}
                                        disabled={submitting}
                                        isInvalid={!!errors.prcp_trigger_hour_inf}
                                    />
                                    <FieldError message={errors.prcp_trigger_hour_inf?.message} />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group controlId="trigger_hour_sup">
                                    <Form.Label>Heure de fin</Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('trigger_hour_sup')}
                                        disabled={submitting}
                                        isInvalid={!!errors.trigger_hour_sup}
                                    />
                                    <FieldError message={errors.trigger_hour_sup?.message} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <div className="scc-actions" role="group" aria-label="Actions du formulaire">
                <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={onCancel}
                    disabled={submitting}
                    className="scc-actions__cancel"
                >
                    Annuler
                </Button>
                <Button
                    type="submit"
                    variant="primary"
                    disabled={submitting}
                    className="scc-actions__submit"
                >
                    {submitting ? (
                        <>
                            <Spinner size="sm" animation="border" className="me-2" />
                            Création en cours…
                        </>
                    ) : (
                        'Créer la sous-couverture'
                    )}
                </Button>
            </div>
        </Form>
    );
};

export default SubCoverageCreateForm;
